import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Plus,
  Users,
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Trash2,
  MoreHorizontal,
  UserCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { KPI } from '@/components/ui/kpi';
import { Pill } from '@/components/ui/pill';
import { Eyebrow } from '@/components/ui/eyebrow';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useAuth } from '@/lib/auth';
import {
  useEmpregados,
  useEmpregadosAggregate,
  useDeleteEmpregado,
  type Empregado,
} from '@/lib/hooks/empregados';
import { EmpregadoDrawer } from '@/features/empregados/EmpregadoDrawer';
import { fmtBRL, fmtCpfCnpj, fmtDate } from '@/lib/utils';

export const Route = createFileRoute('/_app/lancamentos/empregados')({
  component: EmpregadosPage,
});

const PAGE_SIZE = 25;

function EmpregadosPage() {
  const { user } = useAuth();
  const workspaceId = user?.effectiveWorkspaceId ?? null;

  const [search, setSearch] = useState('');
  const [showAtivos, setShowAtivos] = useState(true);
  const [offset, setOffset] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Empregado | null>(null);

  const filters = useMemo(
    () => ({
      search: search.trim() || undefined,
      ativos: showAtivos || undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [search, showAtivos, offset],
  );

  const listQ = useEmpregados(workspaceId, filters);
  const aggQ = useEmpregadosAggregate(workspaceId);
  const delMut = useDeleteEmpregado(workspaceId);

  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  async function handleDelete() {
    if (!confirmDelete) return;
    try {
      await delMut.mutateAsync(confirmDelete.id);
      toast.success('Empregado removido.');
      setConfirmDelete(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao remover.');
    }
  }

  if (!workspaceId) {
    return (
      <Card variant="default" className="p-8 text-center">
        <p className="text-sm text-fg-muted">Selecione um workspace para gerenciar empregados.</p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow>Lançamento · Mensal</Eyebrow>
          <h1 className="font-display text-[34px] md:text-[42px] font-bold tracking-[-0.03em] text-fg leading-[1.0] mt-1">
            Empregados
          </h1>
          <p className="text-sm text-fg-muted mt-2">Quadro vinculado ao convênio</p>
        </div>
        <Button variant="primary" size="lg" onClick={() => setDrawerOpen(true)}>
          <Plus className="h-4 w-4" strokeWidth={1.8} />
          Novo empregado
        </Button>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KPI
          tone="mint"
          label="Ativos"
          value={String(aggQ.data?.ativos ?? 0)}
          delta={aggQ.data?.ativos === 1 ? 'empregado' : 'empregados'}
          icon={UserCheck}
        />
        <KPI label="Resultados visíveis" value={String(total)} delta={showAtivos ? 'só ativos' : 'todos'} icon={Users} />
        <KPI tone="ink" label="Pessoas-chave" value="—" delta="em breve" />
      </section>

      <Card variant="default" className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-fg-muted" strokeWidth={1.8} />
            <Input
              placeholder="Buscar por nome ou CPF…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(0);
              }}
              className="pl-9"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-fg-muted cursor-pointer select-none">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-card-stroke accent-mint-deep"
              checked={showAtivos}
              onChange={(e) => {
                setShowAtivos(e.target.checked);
                setOffset(0);
              }}
            />
            Só ativos
          </label>
        </div>
      </Card>

      <div className="rounded-3 border border-card-stroke bg-card shadow-1 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-card-stroke bg-card-soft/60">
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted">Nome</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted">Cargo</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted">Admissão</th>
                <th className="px-4 py-3 text-right font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted">Salário</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {listQ.isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-fg-muted">
                    <Loader2 className="inline h-4 w-4 animate-spin mr-2" strokeWidth={1.8} />
                    Carregando…
                  </td>
                </tr>
              )}
              {!listQ.isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-fg-muted text-sm">
                    Nenhum empregado encontrado.
                  </td>
                </tr>
              )}
              {items.map((e) => {
                const ativo = !e.dataDesligamento;
                return (
                  <tr key={e.id} className="border-b border-card-stroke/50 last:border-b-0 hover:bg-card-soft/40">
                    <td className="px-4 py-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-fg truncate">{e.nome}</p>
                        <p className="text-[11px] text-fg-muted font-mono">{fmtCpfCnpj(e.cpf)}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-fg-muted">{e.cargo ?? '—'}</td>
                    <td className="px-4 py-3 text-sm text-fg whitespace-nowrap">{e.dataAdmissao ? fmtDate(e.dataAdmissao) : '—'}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm text-fg tabular-nums">{e.salarioBase ? fmtBRL(e.salarioBase) : '—'}</td>
                    <td className="px-4 py-3">
                      <Pill tone={ativo ? 'pass' : 'block'} dot>
                        {ativo ? 'ativo' : 'desligado'}
                      </Pill>
                    </td>
                    <td className="px-4 py-3 text-right relative">
                      <button
                        type="button"
                        onClick={() => setOpenRow(openRow === e.id ? null : e.id)}
                        className="grid h-8 w-8 ml-auto place-items-center rounded-1 text-fg-muted hover:text-fg hover:bg-card-soft"
                        aria-label="Ações"
                      >
                        <MoreHorizontal className="h-4 w-4" strokeWidth={1.8} />
                      </button>
                      {openRow === e.id && (
                        <>
                          <div className="fixed inset-0 z-30" onClick={() => setOpenRow(null)} aria-hidden />
                          <div className="absolute right-2 top-10 z-40 w-40 rounded-2 border border-card-stroke bg-card shadow-clay p-1">
                            <button
                              type="button"
                              onClick={() => {
                                setOpenRow(null);
                                setConfirmDelete(e);
                              }}
                              className="flex w-full items-center gap-2 rounded-1 px-2 py-1.5 text-sm text-warn hover:bg-warn-soft"
                            >
                              <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                              Excluir
                            </button>
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {total > 0 && (
          <div className="flex items-center justify-between gap-2 border-t border-card-stroke px-4 py-3 bg-card-soft/40">
            <p className="text-xs text-fg-muted">
              Página <span className="text-fg font-medium">{currentPage}</span> de{' '}
              <span className="text-fg font-medium">{totalPages}</span> · {total}{' '}
              {total === 1 ? 'pessoa' : 'pessoas'}
            </p>
            <div className="flex items-center gap-1">
              <Button type="button" variant="ghost" size="sm" disabled={!canPrev} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
                <ChevronLeft className="h-4 w-4" strokeWidth={1.8} />
              </Button>
              <Button type="button" variant="ghost" size="sm" disabled={!canNext} onClick={() => setOffset(offset + PAGE_SIZE)}>
                <ChevronRight className="h-4 w-4" strokeWidth={1.8} />
              </Button>
            </div>
          </div>
        )}
      </div>

      <EmpregadoDrawer workspaceId={workspaceId} open={drawerOpen} onOpenChange={setDrawerOpen} />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
        tone="danger"
        title="Excluir este empregado?"
        description={
          confirmDelete
            ? `${confirmDelete.nome} · ${fmtCpfCnpj(confirmDelete.cpf)}. Não dá pra desfazer.`
            : undefined
        }
        confirmLabel="Excluir"
        loading={delMut.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}
